//frontend/src/components/FinanceReport/FinanceReportAccounts/FinanceAccountPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Button from "../../UI/Button";
import Modal from "../../UI/Modal";
import Dropdown from "../../UI/Dropdown";
import ConfirmModal from "../../UI/ConfirmModal";
import { authFetch } from "../../api/api";
import { useTranslation } from "react-i18next";



const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/finances/accounts/`;
const TX_URL = `${BASE_URL}/api/finances/transactions/`;

const emptyForm = {
  type: "income",
  amount: "",
  comment: "",
  date: "",
};



/* символы валют */
const currencySymbols = {
  USD: "$",
  EUR: "€",
  RUB: "₽",
  CNY: "¥",
  HKD: "HK$",
  USDT: "₮",
};

export default function FinanceAccountPage() {
  const { id } = useParams();

  const [account, setAccount] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isOpen, setIsOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);

  const [txToDelete, setTxToDelete] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const [rates, setRates] = useState(null);

  const { t } = useTranslation("FinanceReport");
  /* ================= LOAD ACCOUNT ================= */
  const fetchAccount = async () => {
    const res = await authFetch(`${API_URL}${id}/`);
    const data = await res.json();
    setAccount(data);
  };


const types = useMemo(() => [

  { id: "income", label: t("income") },

  { id: "expense", label: t("expense") },

  { id: "dividends", label: t("dividends") },

], [t]);

  useEffect(() => {
    fetchAccount();
  }, [id]);

  /* ================= RATES (USD → CNY) ================= */
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await authFetch("https://open.er-api.com/v6/latest/USD");
        const data = await res.json();

        const usdToCny = data.rates.CNY;

        const normalized = {
          USD: usdToCny,
          EUR: usdToCny / data.rates.EUR,
          RUB: usdToCny / data.rates.RUB,
          HKD: usdToCny / data.rates.HKD,
          USDT: usdToCny,
          CNY: 1,
        };

        setRates(normalized);
      } catch (e) {
        console.error(e);
      }
    };

    fetchRates();
  }, []);

  /* ================= TOTAL IN CNY ================= */
  const totalCny = useMemo(() => {
    if (!account || !rates) return 0;

    return account.transactions.reduce((sum, tx) => {
      const rate = rates[account.currency] || 0;
      const val = Number(tx.amount || 0) * rate;

      return tx.type === "income"
        ? sum + val
        : sum - val;
    }, 0);
  }, [account, rates]);

  /* ================= SAVE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const url = editingTx
      ? `${TX_URL}${editingTx}/`
      : TX_URL;

    const method = editingTx ? "PUT" : "POST";

    const payload = {
      ...form,
      account: id,
    };

    if (!payload.date) delete payload.date;

    await authFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    setIsOpen(false);
    setForm(emptyForm);
    setEditingTx(null);
    fetchAccount();
  };

  /* ================= EDIT ================= */
  const openEdit = (tx) => {
    setForm({
      type: tx.type,
      amount: tx.amount,
      comment: tx.comment || "",
      date: tx.date || "",
    });
    setEditingTx(tx.id);
    setIsOpen(true);
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    await authFetch(`${TX_URL}${txToDelete.id}/`, {
      method: "DELETE",
    });

    setShowDelete(false);
    setTxToDelete(null);
    fetchAccount();
  };

  if (!account) return <div>{t("loading")}</div>;

  return (
    <div className="max-w-4xl mx-auto mt-10">

      {/* HEADER */}
      <h1 className="text-2xl font-bold">{account.name}</h1>

      <p className="text-gray-600 mb-2">
        {t("balance")} : {" "}
        {currencySymbols[account.currency] || account.currency}{" "}
        {account.balance}
      </p>

      {/* TOTAL IN CNY */}
      <p className="mb-4 font-semibold text-gray-800">
        ≈ ¥ {totalCny.toFixed(2)} (CNY)
      </p>

      <Button onClick={() => setIsOpen(true)} variant="primary">
        {t("add transaction")}
      </Button>

      {/* ================= TABLE ================= */}
      <div className="mt-6 border rounded-xl overflow-hidden">

        <div className="grid grid-cols-4 bg-gray-100 p-3 font-semibold">
          <div>{t("date")}</div>
          <div>{t("sum")}</div>
          <div>{t("comment")}</div>
          <div className="text-right"></div>
        </div>

        {account.transactions.map((tx) => (
          <div
            key={tx.id}
            className="grid grid-cols-4 p-3 border-t items-center"
          >
            <div>{tx.date || "-"}</div>

            <div
            className={
              tx.type === "income"
                ? "text-green-600"
                : tx.type === "dividends"
                ? "text-blue-600"
                : "text-red-600"
            }
            >
              {tx.type === "income" ? "+" : "-"}
              {currencySymbols[account.currency] || ""}
              {Number(tx.amount).toFixed(2)}
            </div>
            <div className="truncate">{tx.comment || "-"}</div>

            <div className="flex gap-2 justify-end">
              <Button variant="edit" onClick={() => openEdit(tx)}>
                {t("edit")}
              </Button>

              <Button
                variant="danger"
                onClick={() => {
                  setTxToDelete(tx);
                  setShowDelete(true);
                }}
              >
                {t("delete")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingTx ? t("edit") : t("add transaction")}
      >

      <style>
      {`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}
      </style>

        <form onSubmit={handleSubmit} className="space-y-3">

          <Dropdown
            value={form.type}
            options={types}
            getLabel={(o) => o.label}
            onChange={(v) => setForm({ ...form, type: v })}
          />

          <input
            type="number"
            className="w-full border p-2 rounded-xl"
            placeholder={t("sum")}
            value={form.amount}
            onChange={(e) =>
              setForm({ ...form, amount: e.target.value })
            }
            required
          />

          <input
            type="date"
            className="w-full border p-2 rounded-xl"
            value={form.date}
            onChange={(e) =>
              setForm({ ...form, date: e.target.value })
            }
          />

          <input
            className="w-full border p-2 rounded-xl"
            placeholder={t("comment")}
            value={form.comment}
            onChange={(e) =>
              setForm({ ...form, comment: e.target.value })
            }
          />

          <button className="w-full bg-black text-white py-2 rounded-full">
            {editingTx ? t("save") : t("add")}
          </button>
        </form>
      </Modal>

      {/* DELETE */}
      <ConfirmModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Удалить транзакцию?"
        description="Это действие нельзя отменить"
        confirmText="Удалить"
      />
    </div>
  );
}